import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { LINKS, openExternalLink } from '../config/links';
import { colors, shared } from '../styles/theme';
import PlatformIcon from '../components/PlatformIcon';

function Customers() {
  return (
    <section className={css(styles.page)}>
      <div className={css(styles.hero)}>
        <p className={css(styles.eyebrow)}>Customers</p>
        <h1 className={css(styles.title)}>Order on YaBatho.</h1>
        <p className={css(styles.copy)}>
          Use the customer web app or download YaBatho from the App Store,
          Google Play, or Huawei AppGallery.
        </p>
        <div className={css(styles.actions)}>
          <button
            className={css(styles.primaryButton)}
            onClick={() => openExternalLink(LINKS.customerWebApp)}
          >
            <PlatformIcon platform="web" inverse />
            <span>Open Customer Web App</span>
          </button>
          <button
            className={css(styles.secondaryButton)}
            onClick={() => openExternalLink(LINKS.playStore)}
          >
            <PlatformIcon platform="android" />
            <span>Google Play</span>
          </button>
          <button
            className={css(styles.secondaryButton)}
            onClick={() => openExternalLink(LINKS.appGallery)}
          >
            <PlatformIcon platform="huawei" />
            <span>Huawei AppGallery</span>
          </button>
          <button
            className={css(styles.secondaryButton)}
            onClick={() => openExternalLink(LINKS.appStore)}
          >
            <PlatformIcon platform="ios" />
            <span>Apple App Store</span>
          </button>
        </div>
      </div>

      <article className={css(styles.webAccess)}>
        <div>
          <span className={css(styles.webEyebrow)}>Order online now</span>
          <h2 className={css(styles.webTitle)}>Customer web app</h2>
          <p className={css(styles.webText)}>
            Start ordering instantly from any browser—no download needed and
            no device restrictions.
          </p>
        </div>
        <button
          className={css(styles.webButton)}
          onClick={() => openExternalLink(LINKS.customerWebApp)}
        >
          <PlatformIcon platform="web" inverse />
          <span>Open Customer Web App</span>
        </button>
      </article>

      <div className={css(styles.mobileAppsHeading)}>
        <span className={css(styles.eyebrow)}>Download the mobile app</span>
        <h2 className={css(styles.sectionTitle)}>Choose your app store</h2>
      </div>

      <div className={css(styles.grid)}>
        <article className={css(styles.card)}>
          <span className={css(styles.index)}>01</span>
          <h2 className={css(styles.cardTitle)}>Google Play</h2>
          <p className={css(styles.cardText)}>
            Get YaBatho on Android for quick ordering, convenient updates, and
            easy access to your favourite local restaurants.
          </p>
          <button
            className={css(styles.cardButton)}
            onClick={() => openExternalLink(LINKS.playStore)}
          >
            <PlatformIcon platform="android" />
            <span>Open Google Play</span>
          </button>
        </article>

        <article className={css(styles.card)}>
          <span className={css(styles.index)}>02</span>
          <h2 className={css(styles.cardTitle)}>Huawei AppGallery</h2>
          <p className={css(styles.cardText)}>
            Enjoy a smooth YaBatho experience on Huawei, with local food,
            simple ordering, and delivery access in one app.
          </p>
          <button
            className={css(styles.cardButton)}
            onClick={() => openExternalLink(LINKS.appGallery)}
          >
            <PlatformIcon platform="huawei" />
            <span>Open AppGallery</span>
          </button>
        </article>

        <article className={css(styles.card)}>
          <span className={css(styles.index)}>03</span>
          <h2 className={css(styles.cardTitle)}>Apple App Store</h2>
          <p className={css(styles.cardText)}>
            Bring YaBatho to your iPhone for a fast, familiar way to discover
            local restaurants and order wherever you are.
          </p>
          <button
            className={css(styles.cardButton)}
            onClick={() => openExternalLink(LINKS.appStore)}
          >
            <PlatformIcon platform="ios" />
            <span>Open App Store</span>
          </button>
        </article>
      </div>
    </section>
  );
}

const styles = StyleSheet.create({
  page: {
    ...shared.navPage,
  },
  hero: {
    maxWidth: '860px',
    marginBottom: '34px',
  },
  eyebrow: shared.eyebrow,
  title: {
    margin: '0 0 18px',
    color: colors.ink,
    fontSize: '3.2rem',
    fontWeight: '900',
    lineHeight: 1.05,
    '@media (max-width: 520px)': {
      fontSize: '2rem',
      lineHeight: 1.08,
    },
  },
  copy: {
    margin: '0 0 26px',
    color: colors.muted,
    fontSize: '1.06rem',
    lineHeight: 1.8,
    '@media (max-width: 520px)': {
      fontSize: '0.98rem',
      lineHeight: 1.65,
    },
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    width: '100%',

    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  primaryButton: {
    ...shared.primaryButton,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    minHeight: '62px',
    width: '100%',
    boxSizing: 'border-box',
  },
  secondaryButton: {
    ...shared.secondaryButton,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    minHeight: '62px',
    width: '100%',
    boxSizing: 'border-box',
  },
  webAccess: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '28px',
    marginBottom: '42px',
    padding: '28px',
    borderRadius: '8px',
    backgroundColor: colors.ink,
    color: colors.white,

    '@media (max-width: 680px)': {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  webEyebrow: {
    color: colors.gold,
    fontSize: '0.82rem',
    fontWeight: '900',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  webTitle: {
    margin: '8px 0',
    fontSize: '1.65rem',
  },
  webText: {
    maxWidth: '680px',
    margin: 0,
    color: '#F4EBD8',
    lineHeight: 1.65,
  },
  webButton: {
    ...shared.primaryButton,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexShrink: 0,
    gap: '10px',
    minHeight: '62px',
  },
  mobileAppsHeading: {
    marginBottom: '20px',
  },
  sectionTitle: {
    margin: '8px 0 0',
    color: colors.ink,
    fontSize: '1.8rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '16px',

    '@media (max-width: 1080px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },

    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 10px 28px rgba(30, 30, 30, 0.05)',
  },
  index: {
    color: colors.gold,
    fontWeight: '900',
    letterSpacing: '1px',
  },
  cardTitle: {
    margin: '14px 0 10px',
    color: colors.ink,
    fontSize: '1.18rem',
  },
  cardText: {
    margin: '0 0 18px',
    color: colors.muted,
    lineHeight: 1.65,
    flexGrow: 1,
  },
  cardButton: {
    ...shared.secondaryButton,
    padding: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    minHeight: '58px',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '0.95rem',
    lineHeight: 1.25,
    textAlign: 'left',
  },
});

export default Customers;
